import {useCallback, useMemo, useRef} from 'react';

import {isAndroidInAppBrowser} from '@helpers';
import {UseFBKeyboardAvoidingView} from './types';

const POSITION_GAP = 64;
const PERCENTAGE_SCREEN_KEYBOARD = 0.38;

/*
 * keyboard avoiding view libs don't work on Android WebViews (facebook in-app browser)
 * this solution is far from perfect but is the best we can do at the moment
 * */
export const useFBKeyboardAvoidingView: UseFBKeyboardAvoidingView = (scrollRef, opts = {}) => {
  const {offset = 0, gap = POSITION_GAP, delay = 0} = opts;

  const elements = useRef<Record<string, number>>({});

  const scrollTo = useCallback(
    (y: number) => {
      // sometimes fails, this setTimeout is a workaround to make it working
      setTimeout(() => scrollRef.current?.scrollTo({y}), delay);
    },
    [scrollRef, delay],
  );

  const setElementPosition = useCallback(
    (elementId: string, y: number) => {
      if (!isAndroidInAppBrowser) return;
      if (!elements.current[elementId]) {
        elements.current[elementId] = Math.round(y + gap - offset);
      }
    },
    [offset, gap],
  );

  const scrollToElement = useCallback(
    (elementId: string) => {
      if (!isAndroidInAppBrowser) return;
      const position = elements.current[elementId];
      if (position) scrollTo(position);
    },
    [scrollTo],
  );

  const restoreScroll = useCallback(() => {
    if (!isAndroidInAppBrowser) return;
    const keyboardHeight = window.innerHeight * PERCENTAGE_SCREEN_KEYBOARD;
    scrollTo(-keyboardHeight);
  }, [scrollTo]);

  return useMemo(
    () => ({setElementPosition, scrollToElement, restoreScroll}),
    [setElementPosition, scrollToElement, restoreScroll],
  );
};
